// filepath: apps/web/src/renderers/form/fields/EnumField.tsx
//
// Single-select dropdown.
//
// IMPORTANT — ISS-038 / D-1: the backend FieldMetadata shape does NOT carry
// an `enumValues` or `options` array. The renderer reads the option list
// from one of two places, in priority order:
//
//   1. `metadata.options` — if present (renderer extension).
//   2. `ui_schema.fields[<fieldId>].options` — the ISS-038 option (b)
//      workaround path.
//   3. Otherwise: degrade to a free-text <input type="text"> and emit
//      console.warn — the value still saves, but the user has no guidance
//      on allowed values. See impact analysis §10.2/D-1.
//
// SECURITY (INV-9): option values are rendered as text nodes inside
// <option> elements — never HTML.

import React from "react";
import type { FieldComponentProps } from "../FieldRegistry";
import { getUiSchemaField } from "../FieldRegistry";
import type { UiSchema } from "@/definitions/types";
import { useTranslation } from "@/i18n/useTranslation";
import { resolveLocalizedValue } from "@/i18n/localeMap";
import { useIsMobile, MIN_TOUCH_TARGET_PX } from "@/runtime/useIsMobile";

export interface EnumFieldProps extends FieldComponentProps {
  /** Optional UI schema for this form. Supplied by the FormRenderer. */
  uiSchema?: UiSchema | null;
}

function resolveOptions(
  fieldId: string,
  metadata: { options?: string[] },
  uiSchema?: UiSchema | null,
): string[] | undefined {
  if (metadata.options && metadata.options.length > 0) {
    return metadata.options;
  }
  const uiOptions = uiSchema?.fields?.[fieldId]?.options;
  if (uiOptions && uiOptions.length > 0) {
    return uiOptions;
  }
  return undefined;
}

export function EnumField({
  fieldId,
  metadata,
  value,
  onChange,
  uiSchema,
  disabled,
}: EnumFieldProps): React.ReactElement {
  const { locale, t } = useTranslation();
  const isMobile = useIsMobile();
  const uiField = getUiSchemaField(uiSchema, fieldId);
  const labelText = resolveLocalizedValue(uiField?.label, locale, fieldId);
  const helpText = resolveLocalizedValue(uiField?.help, locale, "");
  const helpId = `field-help-${fieldId}`;
  const describedBy = helpText ? helpId : undefined;
  const options = resolveOptions(fieldId, metadata, uiSchema);

  // FR-UI-NFR-3 AC-3: 48×48dp touch target; AC-5: no text truncation.
  const labelStyle: React.CSSProperties = isMobile
    ? { wordBreak: "break-word", overflowWrap: "break-word", fontWeight: 500 }
    : { fontWeight: 500 };
  const selectStyle: React.CSSProperties = isMobile
    ? {
        width: "100%",
        minHeight: `${MIN_TOUCH_TARGET_PX}px`,
        padding: "8px 12px",
        fontSize: "16px",
        boxSizing: "border-box",
      }
    : {};
  const fallbackInputStyle: React.CSSProperties = isMobile
    ? {
        width: "100%",
        minHeight: `${MIN_TOUCH_TARGET_PX}px`,
        padding: "8px 12px",
        fontSize: "16px",
        boxSizing: "border-box",
      }
    : {};

  if (!options) {
    // eslint-disable-next-line no-console
    console.warn(
      `[EnumField] field "${fieldId}" has no options (neither metadata.options nor ui_schema.fields.${fieldId}.options). ` +
        `Falling back to free-text input until the backend wire format is extended (ISS-038).`,
    );
    return (
      <div
        data-testid={`field-${fieldId}`}
        className="field field-enum field-enum-fallback"
      >
        <label htmlFor={fieldId} style={labelStyle}>
          {labelText}
          {uiField?.required ? <span aria-hidden="true"> *</span> : null}
        </label>
        <input
          id={fieldId}
          type="text"
          name={fieldId}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-required={uiField?.required ? "true" : undefined}
          aria-describedby={describedBy}
          data-fallback="enum-no-options"
          data-testid={`input-${fieldId}`}
          style={fallbackInputStyle}
        />
        {helpText ? (
          <p
            id={helpId}
            className="field-help"
            style={
              isMobile
                ? { wordBreak: "break-word", overflowWrap: "break-word" }
                : {}
            }
          >
            {helpText}
          </p>
        ) : null}
        <span
          className="field-warning"
          data-testid={`field-${fieldId}-warning`}
        >
          {t("common.error")}: {t("common.optional")}
        </span>
      </div>
    );
  }

  return (
    <div data-testid={`field-${fieldId}`} className="field field-enum">
      <label htmlFor={fieldId} style={labelStyle}>
        {labelText}
        {uiField?.required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <select
        id={fieldId}
        name={fieldId}
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-required={uiField?.required ? "true" : undefined}
        aria-describedby={describedBy}
        data-testid={`select-${fieldId}`}
        style={selectStyle}
      >
        <option value="" disabled>
          — {t("common.search")} —
        </option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      {helpText ? (
        <p
          id={helpId}
          className="field-help"
          style={
            isMobile
              ? { wordBreak: "break-word", overflowWrap: "break-word" }
              : {}
          }
        >
          {helpText}
        </p>
      ) : null}
    </div>
  );
}
